/* eslint-disable max-lines */
import {
  DeleteOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  PlusSquareOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { faMessageLines } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SheetThreadsDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { canViewRecordNode, useMe } from '@salescore/client-common'
import { openRecordComments, useAppDispatch } from '@salescore/client-redux'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { ButtonWithTooltip, disableOnClickOutsideProperties, organizationHasFeature } from '@salescore/frontend-common'
import { App, Button, Dropdown } from 'antd'
import { HashMap, Option, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, type ReactNode, type RefObject, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { KpiAdditionForm } from '../../../components/view_ui/ViewUIKpiPivot/KpiAdditionForm'
import { buildViewDebugModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/ViewDebugModal'
import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { getArea } from '../../../domain/cursor/cursorHelpers'
import type { RSheetsCursor } from '../../../domain/cursor/cursorTypes'
import { isKpiOrKpiPivotContext, type RSheetContext } from '../../../recoil/context'
import {
  getInnerRecordNodeWithPreviousAndNext,
  useCursorModel,
  useCursorMutation,
  useGetInnerCells,
  useMoveToRowIndexAndEditModeMutation,
  useRowExpandable,
} from '../../../recoil/cursor'
import { useSetRowConfig } from '../../../recoil/navigation/hooks'
import { useRecordsImpl } from '../../../recoil/records'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation'
import { rowConfigsImplAtom } from '../../../recoil/rowConfig'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
import { usePasteWithNewRecord } from '../../recoil/hooks/usePasteWithNewRecord'
import { useClickCurrentCursorMutation } from '../../recoil/mutations/cursor/clickCurrentCursorMutation'
import { useRSheetMutation } from '../../recoil/mutations/useRSheetMutation'
import { getRecordNodeWithInnerRowIndex } from '../../util/innerRowRelated'
import { RSheetsStyle } from '../../util/RSheetsStyle'
import { ContextMenu as CommonContextMenu, useKpiContextMenu } from '../common/ContextMenu'
import { buildConvertLeadFormModalProperties } from '../modal/ConvertLeadFormModal'

export const ContextMenu = ({
  rootReference,
  context,
  columns,
  compiledConfig,
  view,
  ability,
  query,
  visibleRowNumber,
  setHasChangeToViewSchema,
}: {
  rootReference: RefObject<HTMLDivElement>
  context: RSheetContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  visibleRowNumber: number | undefined
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
  // eslint-disable-next-line complexity
}): ReactElement => {
  const { modal } = App.useApp()
  const cursor = useCursorModel(columns)
  const rows = useRecordsImpl()
  const row = cursor === undefined ? undefined : rows[cursor.main.rowIndex]
  const column = cursor === undefined ? undefined : columns[cursor.main.columnIndex]
  const { hideContextMenu } = useCursorMutation(ability)
  const me = useMe()
  const rowConfigsImpl = useRecoilValue(rowConfigsImplAtom)
  const rowConfig = useMemo(
    () => pipe(rowConfigsImpl, HashMap.get(row?.id ?? ''), Option.getOrUndefined),
    [row?.id, rowConfigsImpl],
  )
  const setRowConfig = useSetRowConfig()
  const rowExpandable = useRowExpandable(columns, visibleRowNumber)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const expandable = rowExpandable(row!)
  const getInnerCells = useGetInnerCells(visibleRowNumber)
  const mutation = useRSheetMutation(rootReference, columns, compiledConfig, view, ability, query)
  const [addKpiDropdownVisible, setAddKpiDropdownVisible] = useState(false)
  const record =
    row !== undefined && column !== undefined && cursor !== undefined
      ? getRecordNodeWithInnerRowIndex(row, column.node, cursor.main.innerRowIndex)
      : undefined
  const cellValue =
    row !== undefined && column !== undefined && cursor?.main !== undefined
      ? getInnerRecordNodeWithPreviousAndNext(getInnerCells(row, column), cursor.main.innerRowIndex).current?.value
      : undefined
  const kpiId = context.type === 'kpiPivot' ? context.kpis.find((x) => x.id === cellValue)?.id : undefined
  const kpiContextMenu = useKpiContextMenu({
    kpiId,
    hideContextMenu: () => {
      hideContextMenu()
    },
    setAddKpiDropdownVisible,
    addKpiDirection: 'down',
    view,
    setHasChangeToViewSchema,
  })

  if (context.type === 'sheet' && context.isGoalView) {
    return <></>
  }

  const contextMenuPosition = cursor?.contextMenuPosition
  if (contextMenuPosition === undefined || row === undefined || column === undefined || cursor === undefined) {
    return <></>
  }

  if (isKpiOrKpiPivotContext(context)) {
    if (column.type !== 'rowKpi') {
      return <></>
    }
    return (
      <>
        <CommonContextMenu
          menu={kpiContextMenu}
          hideContextMenu={() => {
            setAddKpiDropdownVisible(false)
            hideContextMenu()
          }}
          style={{
            position: 'fixed',
            width: 180,
            left: contextMenuPosition.x + 10,
            top: contextMenuPosition.y - 10,
          }}
        />
        {addKpiDropdownVisible && kpiId !== undefined && (
          <div
            {...disableOnClickOutsideProperties}
            className="absolute top-0 bg-white p-2"
            style={{
              position: 'fixed',
              width: 320,
              left: contextMenuPosition.x + 191, // right: 0もありだが、固定カラムの右隣で実行すると被る問題がz-indexの関係的に解決できなかった
              top: contextMenuPosition.y - 10,
              backgroundColor: 'white',
              boxShadow: '0 2px 6px 2px rgb(60 64 67 / 15%)',
              borderRadius: 2,
              zIndex: RSheetsStyle.zIndex.contextMenu,
            }}
          >
            <KpiAdditionForm
              hide={() => {
                setAddKpiDropdownVisible(false)
                hideContextMenu()
              }}
              prevKpiId={kpiId}
              view={view}
            />
          </div>
        )}
      </>
    )
  }

  const deleteRecordsMenuContents = (
    <>
      <DropdownDeleteButton
        column={column}
        onDelete={async () => {
          if (cursor.expand === undefined) {
            return
          }
          const { upRowIndex, upInnerRowIndex, downRowIndex, downInnerRowIndex } = getArea(cursor.main, cursor.expand)
          await mutation.onDestroyRowsFromContextMenu(
            column,
            upRowIndex,
            upInnerRowIndex,
            downRowIndex,
            downInnerRowIndex,
            false,
          )
        }}
        onDeleteWithAllChildren={async () => {
          if (cursor.expand === undefined) {
            return
          }
          const { upRowIndex, upInnerRowIndex, downRowIndex, downInnerRowIndex } = getArea(cursor.main, cursor.expand)
          await mutation.onDestroyRowsFromContextMenu(
            column,
            upRowIndex,
            upInnerRowIndex,
            downRowIndex,
            downInnerRowIndex,
            true,
          )
        }}
      >
        <Button className="w-full justify-start" type="text" icon={<DeleteOutlined />} danger>
          {t(`削除`)}
        </Button>
      </DropdownDeleteButton>
    </>
  )

  const nonDeleteRecordsMenuContents = (
    <>
      {expandable && rowConfig?.expanded !== true && (
        <Button
          className="w-full justify-start"
          type="text"
          icon={<EyeOutlined />}
          onClick={() => {
            setRowConfig(row.id, (x) => ({ ...x, expanded: true }))
          }}
        >
          {t(`子レコードを全て表示`)}
        </Button>
      )}
      {expandable && rowConfig?.expanded === true && (
        <Button
          className="w-full justify-start"
          type="text"
          icon={<EyeOutlined />}
          onClick={() => {
            setRowConfig(row.id, (x) => ({ ...x, expanded: false }))
          }}
        >
          {t(`子レコードの表示を制限`)}
        </Button>
      )}
      <RecordRelated
        recordNode={record?.recordNode}
        column={column}
        mainCursor={cursor.main}
        rootReference={rootReference}
        context={context}
        columns={columns}
        compiledConfig={compiledConfig}
        view={view}
        ability={ability}
        query={query}
        visibleRowNumber={visibleRowNumber}
      />
      {column.configField?.property.modelName === 'salesforce_lead' && record?.recordNode?.id !== undefined && (
        <Button
          className="w-full justify-start"
          type="text"
          icon={<PlayCircleOutlined />}
          onClick={() => {
            const m = modal.confirm(
              buildConvertLeadFormModalProperties({
                query,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                leadIds: [record.recordNode!.id!],
                onFinish: () => {
                  m.destroy()
                },
                compiledConfig,
                view,
              }),
            )
            hideContextMenu()
          }}
        >
          {t(`取引を開始`)}
        </Button>
      )}
      {canViewRecordNode(me) && (
        <Button
          className={`w-full justify-start ${CONSTANT.colorClasses.ADMIN_TEXT_COLOR}`}
          type="text"
          icon={<EyeOutlined />}
          onClick={() => {
            const m = modal.confirm(
              buildViewDebugModalProperties({
                content: record?.recordNode,
                onAfterFinish: () => {
                  m.destroy()
                },
              }),
            )
            hideContextMenu()
          }}
        >
          {t(`レコード表示`)}
        </Button>
      )}
    </>
  )

  return (
    <ContextMenuWrapper contextMenuPosition={contextMenuPosition}>
      {cursor.expand !== undefined && column.deletable !== false
        ? deleteRecordsMenuContents
        : nonDeleteRecordsMenuContents}
    </ContextMenuWrapper>
  )
}

/**
 * コンテキストメニュー表示ラッパー
 * - コンテキストメニューが画面外にはみ出す場合は、位置調整を行う
 * @param contextMenuPosition 右クリックの座標
 */
const ContextMenuWrapper = ({
  children,
  contextMenuPosition,
}: {
  children: ReactNode
  contextMenuPosition: { x: number; y: number }
}): ReactNode => {
  const [contextMenuHeight, setContextMenuHeight] = useState(0)

  // 画面下にはみ出す場合は上にずらして表示する
  const top =
    contextMenuPosition.y + contextMenuHeight > window.innerHeight
      ? contextMenuPosition.y - contextMenuHeight
      : contextMenuPosition.y

  // 画面右にはみ出す場合は左側に表示する
  const width = 220
  const left = contextMenuPosition.x + width > window.innerWidth ? contextMenuPosition.x - width : contextMenuPosition.x
  return (
    <div
      ref={(divElement) => {
        const newHeight = divElement?.offsetHeight ?? 0
        if (contextMenuHeight !== newHeight) {
          setContextMenuHeight(newHeight)
        }
      }}
      style={{
        position: 'fixed',
        width,
        left: left + 10,
        top: top - 10,
        backgroundColor: 'white',
        boxShadow: '0 2px 6px 2px rgb(60 64 67 / 15%)',
        borderRadius: 2,
        zIndex: RSheetsStyle.zIndex.contextMenu,
      }}
    >
      {children}
    </div>
  )
}

const RecordRelated = ({
  recordNode,
  mainCursor,
  column,
  rootReference,
  context,
  columns,
  compiledConfig,
  view,
  ability,
  query,
  visibleRowNumber,
}: {
  recordNode: RSheetRecordNode | undefined
  mainCursor: RSheetsCursor['main']
  column: RSheetColumn<RSheetRecordNode>
  rootReference: RefObject<HTMLDivElement>
  context: RSheetContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  visibleRowNumber: number | undefined
  // eslint-disable-next-line complexity
}): ReactElement => {
  const me = useMe()
  const { views } = useRecoilValue(viewsContextAtom)
  const mutation = useRSheetMutation(rootReference, columns, compiledConfig, view, ability, query)
  const { hideContextMenu, moveToNextInnerRow, hideCopyingArea } = useCursorMutation(ability)
  const moveToRowIndexAndEditModeMutation = useMoveToRowIndexAndEditModeMutation(columns, ability)
  const clickCurrentCursorMutation = useClickCurrentCursorMutation(
    context,
    columns,
    compiledConfig,
    ability,
    query,
    visibleRowNumber,
  )
  const { addRootRecord, addChildRecord } = useAddEmptyRecordMutation(query, ability)
  const paste = usePasteWithNewRecord(columns, ability, query)
  const recordId = recordNode?.id
  const { data } = useQuery(SheetThreadsDocument, {
    variables: {
      organizationId: me.organization.id,
      recordIds: recordId === undefined ? [] : [recordId],
    },
    fetchPolicy: 'cache-only',
  })
  const threads = data?.sheetThreads ?? []
  const dispatch = useAppDispatch()
  const commentFeatureEnabled = organizationHasFeature(me.organization, 'enable_slack_comment')
  // NOTE: NX様専用フラグ(レコードの追加/削除をメニューから非表示にするために使用)
  const enableNxOnlyFeature =
    organizationHasFeature(me.organization, 'enable_nx_only_feature') && column.node.name === 'salesforce_opportunity'
  const canCreateNewRecord =
    (ability.canSaveRecord && ability.canCreateNewRecord) || ability.canCreateSalescoreObjectRecord
  const canDeleteRecord = (ability.canSaveRecord && ability.canDeleteRecord) || ability.canDeleteSalescoreObjectRecord

  if (column.nodeType === 'root' || column.nodeType === undefined) {
    return (
      <>
        {!enableNxOnlyFeature && (
          <>
            <ButtonWithTooltip
              className="w-full justify-start"
              disabled={!canCreateNewRecord}
              showTooltip={!canCreateNewRecord}
              tooltipTitle={t(`権限がないため、追加できません`)}
              type="text"
              icon={<PlusOutlined />}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
              onClick={async () => {
                // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
                const { rowIndex } = mainCursor
                const newRowIndex = await addRootRecord({ rowIndex: mainCursor.rowIndex })
                if (newRowIndex === undefined) {
                  // 権限の問題で失敗したということのはず
                  return
                }
                moveToRowIndexAndEditModeMutation(newRowIndex)
              }}
            >
              {t(`レコードの追加`)}
            </ButtonWithTooltip>
            <ButtonWithTooltip
              className="w-full justify-start"
              disabled={!canCreateNewRecord}
              showTooltip={!canCreateNewRecord}
              tooltipTitle={t(`権限がないため、追加できません`)}
              type="text"
              icon={<PlusSquareOutlined />}
              onClick={() => {
                void paste({
                  rowIndex: mainCursor.rowIndex,
                  innerRowIndex: mainCursor.innerRowIndex,
                  node: column.node,
                })
              }}
            >
              {t(`レコードを追加してペースト`)}
            </ButtonWithTooltip>
            <DropdownDeleteButton
              column={column}
              onDelete={() => {
                void mutation.onDestroyFromContextMenu(column)
              }}
              onDeleteWithAllChildren={() => {
                void mutation.onDestroyFromContextMenu(column, true)
              }}
            >
              <ButtonWithTooltip
                className="w-full justify-start"
                disabled={!canDeleteRecord || column.deletable === false}
                showTooltip={!canDeleteRecord}
                tooltipTitle={t(`権限がないため、削除できません`)}
                type="text"
                danger
                icon={<DeleteOutlined />}
              >
                {t('削除')}
              </ButtonWithTooltip>
            </DropdownDeleteButton>
          </>
        )}
        {commentFeatureEnabled && views[0]?.type === 'sheet' && (
          <ButtonWithTooltip
            className="w-full justify-start"
            type="text"
            disabled={!commentFeatureEnabled}
            showTooltip={!commentFeatureEnabled}
            tooltipTitle={t(`コメント機能が有効化されていません`)}
            icon={<FontAwesomeIcon icon={faMessageLines} />}
            onClick={() => {
              if (recordId === undefined) {
                return
              }
              dispatch(
                openRecordComments({
                  viewId: view.id,
                  recordId,
                  threads,
                }),
              )
              hideContextMenu()
            }}
          >
            {t(`レコードにコメントする`)}
          </ButtonWithTooltip>
        )}
      </>
    )
  }

  if (column.nodeType === 'children') {
    return (
      <>
        {!enableNxOnlyFeature && (
          <>
            <ButtonWithTooltip
              className="w-full justify-start"
              disabled={!canCreateNewRecord}
              showTooltip={!canCreateNewRecord}
              tooltipTitle={t(`権限がないため、追加できません`)}
              type="text"
              icon={<PlusOutlined />}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
              onClick={async () => {
                await addChildRecord({
                  rowIndex: mainCursor.rowIndex,
                  innerRowIndex: mainCursor.innerRowIndex,
                  node: column.node,
                  additionalFieldValues: [],
                })
                moveToNextInnerRow()
                hideCopyingArea()
                hideContextMenu()
              }}
            >
              {t(`レコードの追加`)}
            </ButtonWithTooltip>
            <ButtonWithTooltip
              className="w-full justify-start"
              disabled={!canCreateNewRecord}
              showTooltip={!canCreateNewRecord}
              tooltipTitle={t(`権限がないため、追加できません`)}
              type="text"
              icon={<PlusSquareOutlined />}
              onClick={() => {
                void paste({
                  rowIndex: mainCursor.rowIndex,
                  innerRowIndex: mainCursor.innerRowIndex,
                  node: column.node,
                })
              }}
            >
              {t(`レコードを追加してペースト`)}
            </ButtonWithTooltip>
          </>
        )}
        {recordNode !== undefined && !enableNxOnlyFeature && (
          <>
            <DropdownDeleteButton
              column={column}
              onDelete={() => {
                void mutation.onDestroyFromContextMenu(column)
              }}
              onDeleteWithAllChildren={() => {
                void mutation.onDestroyFromContextMenu(column, true)
              }}
            >
              <ButtonWithTooltip
                className="w-full justify-start"
                disabled={!canDeleteRecord || column.deletable === false}
                showTooltip={!canDeleteRecord}
                tooltipTitle={t(`権限がないため、削除できません`)}
                type="text"
                danger
                icon={<DeleteOutlined />}
              >
                {t(`削除`)}
              </ButtonWithTooltip>
            </DropdownDeleteButton>
          </>
        )}
      </>
    )
  }

  if (column.nodeType === 'parent') {
    if (recordNode === undefined) {
      return (
        <>
          <Button
            className="w-full justify-start"
            type="text"
            icon={<SearchOutlined />}
            onClick={() => {
              clickCurrentCursorMutation({})
            }}
          >
            {t(`レコードの参照`)}
          </Button>
        </>
      )
    }
    return (
      <>
        <Button
          className="w-full justify-start"
          type="text"
          icon={<StopOutlined />}
          onClick={() => {
            mutation.removeRelation(column)
          }}
        >
          {t(`参照の解除`)}
        </Button>
        {!enableNxOnlyFeature && (
          <>
            <DropdownDeleteButton
              column={column}
              onDelete={() => {
                void mutation.onDestroyFromContextMenu(column)
              }}
              onDeleteWithAllChildren={() => {
                void mutation.onDestroyFromContextMenu(column, true)
              }}
            >
              <ButtonWithTooltip
                className="w-full justify-start"
                disabled={!canDeleteRecord || column.deletable === false}
                showTooltip={!canDeleteRecord}
                tooltipTitle={t(`権限がないため、削除できません`)}
                type="text"
                danger
                icon={<DeleteOutlined />}
              >
                {t(`削除`)}
              </ButtonWithTooltip>
            </DropdownDeleteButton>
          </>
        )}
      </>
    )
  }

  return <></>
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DropdownDeleteButton({
  children,
  column,
  onDelete,
  onDeleteWithAllChildren,
}: {
  children: ReactNode
  column: RSheetColumn<RSheetRecordNode>
  onDelete: () => void | Promise<void>
  onDeleteWithAllChildren: () => void | Promise<void>
}): ReactNode {
  return (
    <Dropdown
      menu={{
        items: [
          {
            key: '1',
            label: (
              <span
                className="inline-block w-full text-red-500"
                onClick={() => {
                  void onDelete()
                }}
              >
                {t(`レコードの削除`)}
              </span>
            ),
          },
          (column.node.children ?? []).length > 0
            ? {
                key: '2',
                label: (
                  <span
                    className="inline-block w-full text-red-500"
                    onClick={() => {
                      void onDeleteWithAllChildren()
                    }}
                  >
                    {t(`レコードの削除(子孫含む)`)}
                  </span>
                ),
              }
            : undefined,
        ].filter(isNotNullish),
      }}
      placement="topRight"
      trigger={['click']}
      popupRender={(item) => <div className="translate-x-full translate-y-full">{item}</div>}
    >
      {children}
    </Dropdown>
  )
}
